import { deriveTitle, extractTags, parseFrontmatter } from '../data/markdown.ts';
import type { LocalNote } from '../data/types.ts';
import type { Folder } from '../../../shared/protocol.ts';
import type { DbConfig, DbFilter, FromClause } from './config.ts';

/**
 * Ausführung der Datenbank-Abfragen. Läuft über die Notizen im Speicher, nicht über
 * den Suchindex — es geht um exakte Werte, nicht um Relevanz.
 *
 * Der Editor lebt außerhalb von React; deshalb hält dieses Modul die Daten und die
 * Aktionen, die die Tabelle auslösen darf, in einer schlanken Registry.
 */

export type DbActions = {
  openNote: (id: string) => void;
  setProperty: (id: string, key: string, value: string) => Promise<void>;
  createRow: (config: DbConfig, group: string | null) => Promise<void>;
};

let notes: LocalNote[] = [];
let folders: Folder[] = [];
let actions: DbActions | null = null;
const listeners = new Set<() => void>();

export function setDbData(nextNotes: LocalNote[], nextFolders: Folder[]): void {
  notes = nextNotes;
  folders = nextFolders;
  for (const listener of listeners) listener();
}

export function setDbActions(next: DbActions): void {
  actions = next;
}

export function dbActions(): DbActions | null {
  return actions;
}

export function onDbData(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function folderName(id: string | null): string {
  if (!id) return '';
  return folders.find((folder) => folder.id === id)?.name ?? '';
}

export function folderByName(name: string): Folder | undefined {
  return folders.find((folder) => folder.name.toLowerCase() === name.toLowerCase());
}

/** Werte werden immer aus dem Markdown abgeleitet, nie separat gehalten. */
export function valueOf(note: LocalNote, key: string): string {
  switch (key) {
    case 'title':
    case 'titel':
      return deriveTitle(note.content);
    case 'tags':
      return extractTags(note.content).join(', ');
    case 'folder':
    case 'ordner':
      return folderName(note.folderId);
    case 'updated':
    case 'geändert':
      return new Date(note.updatedAt).toISOString().slice(0, 10);
    case 'created':
    case 'erstellt':
      return new Date(note.createdAt).toISOString().slice(0, 10);
    default:
      return parseFrontmatter(note.content).data[key] ?? '';
  }
}

function matchesFrom(note: LocalNote, clause: FromClause): boolean {
  switch (clause.kind) {
    case 'all':
      return true;
    case 'folder':
      return folderName(note.folderId).toLowerCase() === clause.name.toLowerCase();
    case 'tag':
      return extractTags(note.content).includes(clause.name);
    default:
      return false;
  }
}

const NUMBER = /^-?\d+(?:[.,]\d+)?$/;

function compare(left: string, right: string): number {
  if (NUMBER.test(left) && NUMBER.test(right)) {
    return Number(left.replace(',', '.')) - Number(right.replace(',', '.'));
  }
  return left.localeCompare(right, 'de', { sensitivity: 'base', numeric: true });
}

function matchesFilter(note: LocalNote, filter: DbFilter): boolean {
  const actual = valueOf(note, filter.key);
  switch (filter.op) {
    case 'empty':
      return actual.trim() === '';
    case 'not empty':
      return actual.trim() !== '';
    case 'contains':
      return actual.toLowerCase().includes(filter.value.toLowerCase());
    case '=':
      return actual.toLowerCase() === filter.value.toLowerCase();
    case '!=':
      return actual.toLowerCase() !== filter.value.toLowerCase();
    case '>':
      return compare(actual, filter.value) > 0;
    case '<':
      return compare(actual, filter.value) < 0;
    case '>=':
      return compare(actual, filter.value) >= 0;
    case '<=':
      return compare(actual, filter.value) <= 0;
    default:
      return true;
  }
}

export type DbGroup = { label: string; rows: LocalNote[] };

export function runQuery(config: DbConfig): DbGroup[] {
  let rows = notes.filter(
    (note) =>
      note.deletedAt === null &&
      config.from.every((clause) => matchesFrom(note, clause)) &&
      config.filters.every((filter) => matchesFilter(note, filter)),
  );

  if (config.sort) {
    const { key, direction } = config.sort;
    rows = [...rows].sort((a, b) => {
      const left = valueOf(a, key);
      const right = valueOf(b, key);
      // Leere Werte stehen immer hinten, egal in welche Richtung sortiert wird.
      if (left === '' && right !== '') return 1;
      if (right === '' && left !== '') return -1;
      const result = compare(left, right);
      return direction === 'desc' ? -result : result;
    });
  } else {
    rows = [...rows].sort((a, b) => b.updatedAt - a.updatedAt);
  }

  if (config.limit !== null) rows = rows.slice(0, config.limit);

  if (!config.group) return [{ label: '', rows }];

  const groups = new Map<string, LocalNote[]>();
  for (const note of rows) {
    const label = valueOf(note, config.group) || 'Ohne Angabe';
    const bucket = groups.get(label);
    if (bucket) bucket.push(note);
    else groups.set(label, [note]);
  }
  return [...groups.entries()].map(([label, entries]) => ({ label, rows: entries }));
}
